import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorLogs: Array<{ timestamp: string; message: string; stack?: string }>;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorLogs: [],
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    const errorLog = {
      timestamp: new Date().toISOString(),
      message: error.toString(),
      stack: error.stack,
    };

    this.setState((prevState) => ({
      error,
      errorInfo,
      errorLogs: [...prevState.errorLogs, errorLog],
    }));
  }

  copyErrorToClipboard = () => {
    const { error, errorInfo } = this.state;
    const errorText = `
Ошибка: ${error?.toString()}

Stack trace:
${error?.stack}

Component stack:
${errorInfo?.componentStack}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      alert('Информация об ошибке скопирована в буфер обмена');
    }).catch(() => {
      alert('Не удалось скопировать. Пожалуйста, сделайте скриншот.');
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#1a1a1a',
          color: '#fff',
          padding: '20px',
          fontFamily: 'monospace',
          overflowY: 'auto',
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
          }}>
            {/* Header */}
            <div style={{
              backgroundColor: '#dc2626',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <span style={{ fontSize: '32px' }}>⚠️</span>
              <div>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                  Произошла ошибка при загрузке приложения
                </h1>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  Приложение не смогло запуститься корректно
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '20px',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                🔄 Перезагрузить страницу
              </button>
              <button
                onClick={this.copyErrorToClipboard}
                style={{
                  backgroundColor: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                📋 Скопировать ошибку
              </button>
            </div>

            {/* Error Message */}
            <div style={{
              backgroundColor: '#2d2d2d',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #404040',
            }}>
              <h2 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                color: '#f87171',
              }}>
                Сообщение об ошибке:
              </h2>
              <pre style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '13px',
                lineHeight: '1.5',
                color: '#fca5a5',
              }}>
                {this.state.error?.toString()}
              </pre>
            </div>

            {/* Stack Trace */}
            {this.state.error?.stack && (
              <div style={{
                backgroundColor: '#2d2d2d',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #404040',
              }}>
                <h2 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  color: '#fbbf24',
                }}>
                  Stack Trace:
                </h2>
                <pre style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: '#d1d5db',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}>
                  {this.state.error.stack}
                </pre>
              </div>
            )}

            {/* Component Stack */}
            {this.state.errorInfo?.componentStack && (
              <div style={{
                backgroundColor: '#2d2d2d',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #404040',
              }}>
                <h2 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  color: '#a78bfa',
                }}>
                  Component Stack:
                </h2>
                <pre style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: '#d1d5db',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            {/* Error Logs */}
            {this.state.errorLogs.length > 0 && (
              <div style={{
                backgroundColor: '#2d2d2d',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #404040',
              }}>
                <h2 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  color: '#60a5fa',
                }}>
                  История ошибок ({this.state.errorLogs.length}):
                </h2>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}>
                  {this.state.errorLogs.map((log, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '12px',
                        paddingBottom: '12px',
                        borderBottom: index < this.state.errorLogs.length - 1 ? '1px solid #404040' : 'none',
                      }}
                    >
                      <div style={{
                        fontSize: '11px',
                        color: '#9ca3af',
                        marginBottom: '4px',
                      }}>
                        {new Date(log.timestamp).toLocaleString('ru-RU')}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#fca5a5',
                      }}>
                        {log.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Help Text */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#1e3a5f',
              borderRadius: '8px',
              border: '1px solid #2563eb',
            }}>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                color: '#93c5fd',
              }}>
                💡 Что делать:
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '13px',
                lineHeight: '1.8',
                color: '#bfdbfe',
              }}>
                <li>Попробуйте перезагрузить страницу</li>
                <li>Очистите кэш браузера (Ctrl+Shift+Delete)</li>
                <li>Проверьте соединение с интернетом</li>
                <li>Если проблема повторяется, скопируйте ошибку и отправьте администратору</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

